<template>
    <v-app class="app globalFont">
        <v-app-bar v-if="topbar" class="app_title" app fixed outlined>
            <v-toolbar-title class="float-left">
                <router-link :to="'/home/'">
                    <v-img contain
                           position="left center"
                           max-height="56px"
                           src="/img/logo.png"></v-img>
                </router-link>{{ title }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn v-if="$oidc.isAuthenticated" icon to="/profile">
                <v-icon color="#ffffff">mdi-account-circle-outline</v-icon>
            </v-btn>
        </v-app-bar>
        <v-main>
            <router-view></router-view>
            <EnrollDialog :key="enrollUi.key" :img-src="enrollUi.imageSrc" />

            <AppNotificationDialog v-model="statusDialogOpen"
                                   :tone="currentTone"
                                   :title="currentTitle"
                                   :message="currentMessage"
                                   :options="{ width: 480, zIndex: 320 }"
                                   @OnOk="dismissStatusDialog"
                                   @OnCancel="dismissStatusDialog" />
        </v-main>

        <app-footer v-if="footer"></app-footer>

        <v-overlay :value="isProcessing" opacity="0.9" z-index="1000">
            <v-row align="center" justify="center" class="fill-height">
                <v-col cols="12" class="text-center white--text">
                    <v-progress-circular indeterminate size="50" color="white"></v-progress-circular>
                    <div class="mt-4 font-weight-bold">{{ processingLabel }}</div>
                    <div class="text-caption">Refreshing in {{ countdown }} seconds</div>
                </v-col>
            </v-row>
        </v-overlay>
    </v-app>
</template>

<script lang="ts">
    import AppFooter from "@/components/FooterNavigation.vue";
    import "./style/app.scss";
    import { app } from "@/store";
    import { enrollUi } from '@/store/enrollUi';
    import { computed, defineComponent, onBeforeUnmount, ref, watch } from "vue";
    import useServiceUpdate from "@/utilities/useServiceUpdate";

    export default defineComponent({
        components: {
            AppFooter,
            EnrollDialog: () => import('@/views/components/Dialogs/EnrollDialog.vue'),
            AppNotificationDialog: () => import('@/views/components/Dialogs/AppNotificationDialog.vue')
        },
        setup() {
            useServiceUpdate(); // sets up auto-refresh behavior
            const isProcessing = computed(() => app.isProcessing);
            const processingLabel = computed(() => app.processingLabel);
            const countdown = computed(() => app.countdown);

            const title = computed<string>(() => app.title ? app.title : "");
            const topbar = computed<boolean>(() => app.showTopBar);
            const footer = computed<boolean>(() => app.showFooter);

            const error = computed<string>(() => app.error ?? "");
            const alarm = computed<string>(() => app.alarm ?? "");
            const success = computed<string>(() => app.success ?? "");
            const alert = computed<string>(() => app.alert ?? "");

            // --- unified dialog state ---
            const statusDialogOpen = ref(false);
            const currentTone = ref<'warning' | 'alarm' | 'alert' | 'success'>('warning');
            const currentTitle = ref<string>('');
            const currentMessage = ref<string>('');
            let errorDismissTimer: ReturnType<typeof setTimeout> | null = null;
            let successDismissTimer: ReturnType<typeof setTimeout> | null = null;

            function clearDialogTimers() {
                if (errorDismissTimer) {
                    clearTimeout(errorDismissTimer);
                    errorDismissTimer = null;
                }

                if (successDismissTimer) {
                    clearTimeout(successDismissTimer);
                    successDismissTimer = null;
                }
            }

            // Priority: alarm > alert > warning(error) > success
            function refreshDialogFromStore() {
                if (alarm.value) {
                    currentTone.value = 'alarm';
                    currentTitle.value = 'Alarm triggered';
                    currentMessage.value = alarm.value;
                    statusDialogOpen.value = true;
                    return;
                }
                if (alert.value) {
                    currentTone.value = 'alert';
                    currentTitle.value = 'Alert';
                    currentMessage.value = alert.value;
                    statusDialogOpen.value = true;
                    return;
                }
                if (error.value) {
                    currentTone.value = 'warning';
                    currentTitle.value = 'Warning';
                    currentMessage.value = error.value;
                    statusDialogOpen.value = true;
                    return;
                }
                if (success.value) {
                    currentTone.value = 'success';
                    currentTitle.value = 'Success';
                    currentMessage.value = success.value;
                    statusDialogOpen.value = true;
                    return;
                }
                // none active
                statusDialogOpen.value = false;
                currentMessage.value = '';
            }

            // Dismiss clears only the active source in priority order
            function dismissStatusDialog() {
                switch (currentTone.value) {
                    case 'alarm': app.alarm = ""; break;
                    case 'alert': app.alert = ""; break;
                    case 'warning': app.error = ""; break;
                    case 'success': app.success = ""; break;
                }
                // refresh will close or move to next pending tone
                refreshDialogFromStore();
            }

            // Watch store fields and update the unified dialog
            watch(() => app.alarm, () => refreshDialogFromStore(), { immediate: true });
            watch(() => app.alert, () => refreshDialogFromStore(), { immediate: true });
            watch(() => app.error, (nv) => {
                refreshDialogFromStore();
                if (!nv) {
                    if (errorDismissTimer) {
                        clearTimeout(errorDismissTimer);
                        errorDismissTimer = null;
                    }
                    app.errorAutoDismiss = true;
                    app.errorAutoDismissExplicit = false;
                    return;
                }

                if (!app.errorAutoDismissExplicit) {
                    app.errorAutoDismiss = true;
                }

                if (app.errorAutoDismiss) {
                    if (errorDismissTimer) {
                        clearTimeout(errorDismissTimer);
                    }
                    const timeoutMs = app.errorAutoDismissMs ?? 10000;
                    errorDismissTimer = setTimeout(() => {
                        app.error = "";
                        errorDismissTimer = null;
                    }, timeoutMs);
                }
            }, { immediate: true });
            watch(() => app.success, (nv) => {
                refreshDialogFromStore();
                if (successDismissTimer) {
                    clearTimeout(successDismissTimer);
                    successDismissTimer = null;
                }
                if (nv) {
                    successDismissTimer = setTimeout(() => {
                        app.success = "";
                        successDismissTimer = null;
                    }, 10000); // auto-dismiss
                }
            }, { immediate: true });

            // Also close if the user clicks out or ESC via v-model
            watch(statusDialogOpen, (open) => {
                if (!open) {
                    // user closed the dialog -> clear whichever tone is active
                    dismissStatusDialog();
                }
            });

            onBeforeUnmount(() => {
                clearDialogTimers();
            });

            return {
                title,
                topbar,
                footer,
                isProcessing,
                processingLabel,
                countdown,
                error,
                alarm,
                success,
                alert,
                statusDialogOpen,
                currentTone,
                currentTitle,
                currentMessage,
                dismissStatusDialog,
                enrollUi
            };
        }
    });
</script>

<style scoped>
    .alert-on-top {
        position: fixed;
        top: 10%;
        left: 5%;
        right: 5%;
        z-index: 9999;
    }

    .custom-alert-close {
        width: 60px;
        height: 60px;
        font-size: 24px;
    }

    .custom-alert-close-icon {
        font-size: 40px;
    }

    @import url('https://fonts.googleapis.com/css2?family=Open+Sans&display=swap');
    /*@import url('https://fonts.googleapis.com/css2?family=Dancing+Script&display=swap');*/
    /*@font-face {
        font-family: 'Dancing Script';
        src: local('Dancing Script'), url('~@assets/fonts/DancingScript.ttf') format('truetype')
    }*/
</style>